import { Flex, Text, VStack } from '@chakra-ui/react';
import { ReactNode, useState } from 'react';

import { LEAccordionItem } from '../../../../../components/LEAccordion';
import { useLEAccordionItemContext } from '../../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import { ItemStatusType } from '../../../../../components/LEAccordion/utils';
import SelfServePayoffFormBody from '../../../../../components/SelfServePayoffFormBody';
import Button from '../../../../../components/ui/buttons/Button';
import { LDFlags } from '../../../../../constants/flags';
import { LienholderRequirement } from '../../../../../gql/generated/graphql';
import { useFlag } from '../../../../../hooks';
import useListingSubmit from '../../../../../hooks/useListingSubmit';
import {
  trackPayoffSelfServeLinkTapped,
  trackPayoffSelfServeModalDismissed,
} from '../../../../../services/analytics/marketplaceEvents';
import { useMarketplaceExternalId } from '../../../../../services/marketplace/externalId';
import PayoffIntelligenceMissingFields, {
  PayoffMissingFieldFormValues,
} from '../../../../LoanApp/components/PayoffIntelligenceMissingFields';
import { PayoffTerm } from '../PayoffTooltipCopy';
import usePayoffMissingFieldsSubmit from '../hooks/usePayoffMissingFieldsSubmit';
import useSelfServePayoffSubmit from '../hooks/useSelfServePayoffSubmit';
import MarketplaceListingConsentCopy, { LIST_BUTTON_COPY } from './MarketplaceListingConsentCopy';
import useMarketplaceListingConsent from './useMarketplaceListingConsent';

const getMissingFieldsBody = (lienholderName?: string | null) => (
  <>
    Your <PayoffTerm /> is what it costs to pay off your lease in full today. Buyers need this
    number before they can make you an offer. We'll pull it straight from{' '}
    {lienholderName ?? 'your lienholder'}.
  </>
);

const SELF_SERVE_BODY =
  'Enter your current payoff amount and the date it is good through. You can find both on your most recent statement from your lienholder.';

type SubmitState = { isSubmitting: boolean; isValid: boolean };

type PayoffItemContentProps = {
  dealId: string;
  missingFields: LienholderRequirement[];
  requiredFields: LienholderRequirement[];
  isSelfServe: boolean;
  onToggle: () => void;
  hideToggle: boolean;
  lienholderName?: string | null;
  lienholderSlug?: string | null;
  retailBookValue?: number | null;
};

const PayoffItemContent = ({
  dealId,
  missingFields,
  requiredFields,
  isSelfServe,
  onToggle,
  hideToggle,
  lienholderName,
  lienholderSlug,
  retailBookValue,
}: PayoffItemContentProps) => {
  const { updateState } = useLEAccordionItemContext();
  const submitMissingFields = usePayoffMissingFieldsSubmit({
    dealId,
    missingFields,
    requiredFields,
  });
  const submitSelfServe = useSelfServePayoffSubmit(dealId);
  const externalId = useMarketplaceExternalId(dealId);
  const { submit: activateNoPrice, isSubmitting: isActivating } = useListingSubmit({
    dealId,
    externalId,
    onListed: () => undefined,
    showErrorToast: true,
  });
  const { consentText, saveConsent } = useMarketplaceListingConsent(dealId);

  const completeAndList = async () => {
    try {
      await saveConsent();
      const listed = await activateNoPrice();
      updateState({ status: listed ? 'complete' : 'error' });
    } catch {
      updateState({ status: 'error' });
    }
  };

  const handleMissingFieldsSubmit = async (values: PayoffMissingFieldFormValues) => {
    updateState({ status: 'loading' });
    const ok = await submitMissingFields(values);
    if (!ok) {
      updateState({ status: 'error' });
      return;
    }
    await completeAndList();
  };

  const handleSelfServeSubmit = async (values: {
    vehiclePayoff: number;
    goodThroughDate: Date;
  }) => {
    updateState({ status: 'loading' });
    const ok = await submitSelfServe(values);
    if (!ok) {
      updateState({ status: 'error' });
      return;
    }
    await completeAndList();
  };

  const submitButton = ({ isSubmitting }: SubmitState): ReactNode => {
    const loading = isSubmitting || isActivating;
    return (
      <VStack spacing={4} align="stretch" w="full">
        <MarketplaceListingConsentCopy consentText={consentText} />
        <Flex justify="center">
          <Button
            variant="primary"
            type="submit"
            isLoading={loading}
            loadingText={isActivating ? 'Listing...' : 'Saving...'}
            isDisabled={loading || !consentText}
            px={10}
          >
            {LIST_BUTTON_COPY}
          </Button>
        </Flex>
      </VStack>
    );
  };

  const toggleLink = hideToggle ? undefined : (
    <Flex justify="center" mt={4} mb={6}>
      <Button variant="link" onClick={onToggle}>
        {isSelfServe ? 'Have us pull your payoff instead' : "I'll get my payoff myself"}
      </Button>
    </Flex>
  );

  return (
    <>
      <Text textAlign="center" mb={4}>
        {isSelfServe ? SELF_SERVE_BODY : getMissingFieldsBody(lienholderName)}
      </Text>
      {isSelfServe ? (
        <SelfServePayoffFormBody
          onSubmit={handleSelfServeSubmit}
          lienholderName={lienholderName}
          lienholderSlug={lienholderSlug}
          retailBookValue={retailBookValue}
          submitContent={submitButton}
          belowFields={toggleLink}
        />
      ) : (
        <PayoffIntelligenceMissingFields
          missingFields={missingFields}
          onSubmit={handleMissingFieldsSubmit}
          submitButton={submitButton}
          belowFields={toggleLink}
        />
      )}
    </>
  );
};

type MarketplacePrelistPayoffItemProps = {
  dealId: string;
  missingFields: LienholderRequirement[];
  requiredFields: LienholderRequirement[];
  lienholderName?: string | null;
  lienholderSlug?: string | null;
  retailBookValue?: number | null;
  initialStatus?: ItemStatusType;
};

const MarketplacePrelistPayoffItem = ({
  dealId,
  missingFields,
  requiredFields,
  lienholderName,
  lienholderSlug,
  retailBookValue,
  initialStatus,
}: MarketplacePrelistPayoffItemProps) => {
  const [isSelfServe, setIsSelfServe] = useState(false);
  const disableSelfServe = useFlag(LDFlags.DISABLE_MARKETPLACE_SELF_SERVE);
  const showSelfServe = !disableSelfServe && isSelfServe;

  const handleToggle = () => {
    if (isSelfServe) {
      trackPayoffSelfServeModalDismissed(dealId);
    } else {
      trackPayoffSelfServeLinkTapped(dealId);
    }
    setIsSelfServe((prev) => !prev);
  };

  return (
    <LEAccordionItem
      accordionKey="payoff"
      title={showSelfServe ? 'ENTER YOUR PAYOFF' : 'HELP US FIND YOUR PAYOFF'}
      initialStatus={initialStatus}
    >
      <PayoffItemContent
        dealId={dealId}
        missingFields={missingFields}
        requiredFields={requiredFields}
        isSelfServe={showSelfServe}
        onToggle={handleToggle}
        hideToggle={disableSelfServe}
        lienholderName={lienholderName}
        lienholderSlug={lienholderSlug}
        retailBookValue={retailBookValue}
      />
    </LEAccordionItem>
  );
};

export default MarketplacePrelistPayoffItem;
